import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, useAuth } from '../../../auth-lib';
import {
  Button, Card, Select, StatusPill, Table, useToast,
} from '../../../design-system';
import '../../styles/TallyPanel.css';

// Writing the firm's clients into a Tally company as party ledgers.
//
// ⚠️ THE OTHER END OF THE MATCHING SCREEN. "Client matching" asks which parties
// in these books are clients of the firm; this asks which clients of the firm
// are missing from these books. One reconciliation, and until now it was only
// readable from one end — the endpoint behind this screen had been shipped with
// no caller at all, which is why nobody had noticed it was writing ledgers with
// an empty name (`Client.displayName`, never `Client.name`).
//
// ⚠️ IT QUEUES, IT DOES NOT WRITE. The machine may be a poll interval away, so
// the server answers 202 with a job id and the outcome arrives later. Saying
// "Done" on that 202 would be a lie about somebody's accounting data, and Tally
// answers HTTP 200 even for an import that failed every row — so the only
// honest result is the RECEIPT, which is what this polls for and shows.

const MANAGER_LEVEL = 3;
// Long enough for a machine that polls on an interval, short enough that a dead
// agent is reported rather than spun on for ever.
const POLL_MS = 2000;
const POLL_LIMIT = 90;

const BASIS_LABEL = {
  linked: 'Already linked',
  gstin: 'GSTIN matches',
  pan: 'PAN matches',
  name: 'Name matches',
};

function TallyPushClients({ connection, onChanged }) {
  const toast = useToast();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState(['Sundry Debtors']);
  const [group, setGroup] = useState('Sundry Debtors');
  const [allowWrites, setAllowWrites] = useState(!!connection.allowWrites);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [enabling, setEnabling] = useState(false);
  // null | { phase: 'queued'|'writing'|'done'|'failed', receipt, error }
  const [run, setRun] = useState(null);
  const timer = useRef(null);

  const canWrite = (user?.role?.level || 0) >= MANAGER_LEVEL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/api/tally/connections/${connection.id}/pushable`);
      setRows(data.clients || []);
      setGroups(data.groups || ['Sundry Debtors']);
      setAllowWrites(!!data.allowWrites);
      // ⚠️ NOTHING IS PRE-TICKED, the rule the matching screen next door learned
      // the hard way: rows that started selected on a plausible theory turned
      // seven of a client's customers into client records in one press. The
      // theory is better here — these really are the firm's own clients — but a
      // pre-ticked list still means the default gesture writes into a client's
      // books without anybody reading it.
      setSelected(new Set());
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not work out which clients are missing');
    } finally {
      setLoading(false);
    }
    // `toast` is a stable context value; listing it re-creates this every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.id]);

  useEffect(() => { load(); }, [load]);
  // Stop polling if the view is left mid-write. The job still runs and its
  // outcome is still recorded — this only drops our watch on it.
  useEffect(() => () => clearTimeout(timer.current), []);

  const enableWrites = async () => {
    setEnabling(true);
    try {
      await apiClient.patch(`/api/tally/connections/${connection.id}`, { allowWrites: true });
      setAllowWrites(true);
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not switch writing on');
    } finally {
      setEnabling(false);
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * Watch the queued job until Tally answers.
   *
   * ⚠️ THE VERDICT IS THE RECEIPT, NOT THE JOB STATUS ALONE. The server already
   * marks the job failed when Tally reports errors or exceptions, so this shows
   * the counts either way — "12 created, 3 combined" is what a person can check
   * against their own screen, and "sent" is not.
   */
  const watch = useCallback((jobId, tries = 0) => {
    timer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/api/tally/jobs/${jobId}`);
        if (data.status === 'succeeded' || data.status === 'failed' || data.status === 'expired') {
          setRun({
            phase: data.status === 'succeeded' ? 'done' : 'failed',
            receipt: data.receipt || null,
            error: data.error || '',
          });
          // The books have changed, and so has "which clients are missing" —
          // though the mirror only catches up once the read-back job the server
          // queued behind this one has run.
          await load();
          onChanged?.();
          return;
        }
        if (data.status === 'claimed') setRun((r) => ({ ...r, phase: 'writing' }));
        if (tries >= POLL_LIMIT) {
          setRun({
            phase: 'failed',
            receipt: null,
            // ⚠️ NOT "IT FAILED". We stopped watching; the job may still run and
            // may still write. Saying otherwise would invite a second push.
            error: 'Still waiting after a few minutes — this machine may be offline. '
              + 'The write may still happen; check the ledgers before pushing again.',
          });
          return;
        }
        watch(jobId, tries + 1);
      } catch {
        watch(jobId, tries + 1);
      }
    }, POLL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const push = async () => {
    if (!selected.size) return;
    setRun({ phase: 'queued', receipt: null, error: '' });
    try {
      const { data } = await apiClient.post(
        `/api/tally/connections/${connection.id}/push-clients`,
        { clientIds: [...selected], group },
      );
      setSelected(new Set());
      if (data.skipped) toast.warning(`${data.skipped} client(s) had no name and were not sent`);
      watch(data.jobId);
    } catch (e) {
      setRun(null);
      toast.danger(e.response?.data?.error || 'Could not queue that push');
    }
  };

  const missing = rows.filter((r) => !r.inTally && !r.blocked);
  const shown = showAll ? rows : missing;
  const busy = run?.phase === 'queued' || run?.phase === 'writing';

  return (
    <>
      <Card>
        <div className="tally-head">
          <div>
            <h3 className="tally-section-title">Clients not in these books</h3>
            <p className="tally-muted">
              Clients you hold here that have no party ledger in{' '}
              <strong>{connection.companyName}</strong>. Pushing creates one for
              each — its name, plus PAN, GSTIN and state wherever you record
              them. Nothing is ticked to begin with.
            </p>
          </div>
          {canWrite && allowWrites && (
            <div className="tally-actions">
              <Select
                aria-label="Group to file them under"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                disabled={busy}
              >
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
              <Button
                onClick={push}
                loading={busy}
                disabled={busy || !selected.size || connection.status !== 'active'}
              >
                {`Push ${selected.size} to Tally`}
              </Button>
            </div>
          )}
        </div>

        {/* ⚠️ THE SWITCH IS SHOWN WHERE IT IS NEEDED, not left to be found. It
            was reachable by no screen at all, so the push behind it could never
            be used — and a disabled button with no explanation is how that stays
            true. The copy states the consequence, not the mechanism. */}
        {!allowWrites && (
          <div className="tally-write-off">
            <span>
              <strong>Writing to Tally is off for this company.</strong>{' '}
              Everything else here only reads. Turning this on lets the firm
              create party ledgers in {connection.companyName}&apos;s books —
              entries a person will see in their own Tally.
            </span>
            {canWrite ? (
              <Button variant="primary" loading={enabling} onClick={enableWrites}>Turn on</Button>
            ) : (
              <small>A manager can switch it on.</small>
            )}
          </div>
        )}

        {allowWrites && !canWrite && (
          <p className="tally-muted">
            Writing is switched on for this company, but pushing is a manager&apos;s
            decision. The list below is what a manager would be asked to send.
          </p>
        )}

        {connection.status !== 'active' && (
          <p className="tally-warn">This connection is {connection.status}, so nothing can be written.</p>
        )}

        {/* Queued → writing → the receipt. Each state says what is true at that
            moment; none of them says "done" before Tally has answered. */}
        {run?.phase === 'queued' && (
          <p className="tally-muted">Queued. Waiting for the machine to pick it up…</p>
        )}
        {run?.phase === 'writing' && (
          <p className="tally-muted">Writing into {connection.companyName}…</p>
        )}
        {run?.phase === 'done' && run.receipt && (
          <p className="tally-muted">
            <strong>Tally accepted it.</strong>{' '}
            {run.receipt.created} created, {run.receipt.combined} matched an existing
            ledger, {run.receipt.altered} altered.
            {' '}The list below refreshes once we have re-read the books.
          </p>
        )}
        {run?.phase === 'failed' && (
          <p className="tally-error">
            {run.error || 'Tally refused the write.'}
            {run.receipt ? ` (${run.receipt.errors} error(s), ${run.receipt.exceptions} exception(s))` : ''}
          </p>
        )}

        <div className="tally-filters">
          <label className="tally-check">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            {' '}Show clients already in these books ({rows.length - missing.length})
          </label>
        </div>

        {loading && !rows.length ? (
          <p className="tally-muted">Loading…</p>
        ) : !shown.length ? (
          <p className="tally-muted">
            {rows.length
              ? 'Every client you hold already has a ledger in these books.'
              : 'You have no clients to push yet.'}
          </p>
        ) : (
          <Table
            columns={[
              ...(canWrite && allowWrites ? [{
                key: 'sel',
                header: (
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={selected.size === missing.length && missing.length > 0}
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && selected.size < missing.length;
                    }}
                    // ⚠️ SELECT-ALL MEANS ALL THE MISSING ONES, never everything
                    // on screen. With "show already there" ticked, the visible
                    // list includes rows that need no write at all.
                    onChange={(e) => setSelected(
                      e.target.checked ? new Set(missing.map((m) => m.clientId)) : new Set(),
                    )}
                  />
                ),
                render: (r) => (
                  <input
                    type="checkbox"
                    aria-label={`Push ${r.name}`}
                    checked={selected.has(r.clientId)}
                    // A client already in the books, or one with nothing to
                    // write, is shown for context — it is not a thing to send.
                    disabled={r.inTally || !!r.blocked}
                    onChange={() => toggle(r.clientId)}
                  />
                ),
              }] : []),
              {
                key: 'name',
                header: 'Client',
                render: (r) => (
                  <>
                    {r.name || <em className="tally-muted">No name</em>}
                    {r.clientCode ? <span className="tally-muted"> · {r.clientCode}</span> : null}
                    {/* ⚠️ A CLOSED OR DORMANT CLIENT IS STILL LISTED, and has to
                        say so. They are not filtered out — a firm may genuinely
                        need the ledger for last year's entries — but pushing one
                        by accident puts a name into a client's live books that
                        the firm stopped acting for, and an unlabelled row is
                        indistinguishable from a working client. */}
                    {r.status && r.status !== 'active' ? (
                      <span className="tally-muted"> · {r.status}</span>
                    ) : null}
                  </>
                ),
              },
              // ⚠️ THE ROW AS IT WILL BE WRITTEN, from the server's own composer.
              // Nobody can read the XML that reaches a client's Tally, so this is
              // the only place a wrong GSTIN can be caught before it is in
              // somebody's books.
              { key: 'gstin', header: 'GSTIN', render: (r) => r.gstin || '—' },
              { key: 'pan', header: 'PAN', render: (r) => r.pan || '—' },
              { key: 'state', header: 'State', render: (r) => r.state || '—' },
              {
                key: 'state2',
                header: 'In Tally',
                render: (r) => {
                  if (r.blocked) return <StatusPill variant="danger" label={r.blocked} />;
                  if (!r.inTally) return <StatusPill variant="warning" label="Missing" />;
                  return (
                    <StatusPill
                      variant="success"
                      label={BASIS_LABEL[r.basis] || 'Already there'}
                    />
                  );
                },
              },
            ]}
            data={shown}
            keyField="clientId"
          />
        )}
      </Card>
    </>
  );
}

export default TallyPushClients;
